import React from 'react';
import { Phone, FileText, Building2, Shield, ExternalLink } from 'lucide-react';
export function InterstateOperatingAuthorityPage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-2691">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-2692">
        <div className="container mx-auto px-4" data-id="element-2693">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-2694">
            Operating Authority
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-2695">
            Interstate Commerce Requirements & Regulations
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-2696">
        <div className="max-w-4xl mx-auto" data-id="element-2697">
          {/* Key Requirement */}
          <div className="bg-blue-50 border-2 border-blue-500 rounded-lg shadow-md p-8 mb-8" data-id="element-2698">
            <div className="flex items-start mb-4" data-id="element-2699">
              <Building2 className="text-blue-600 mr-4 flex-shrink-0 mt-1" size={32} data-id="element-2700" />
              <div data-id="element-2701">
                <h2 className="text-2xl font-bold text-blue-900 mb-4" data-id="element-2702">
                  Who Needs Operating Authority?
                </h2>
                <p className="text-lg text-gray-800 italic" data-id="element-2703">
                  <strong data-id="element-2704">
                    Companies that operate as "for hire" carriers (for a fee or
                    other compensation) that transport passengers or federally
                    regulated commodities, or arrange for their transport, in
                    interstate commerce are also required to have interstate
                    operating authority.
                  </strong>
                </p>
              </div>
            </div>
          </div>

          {/* The Reach of Operating Authority */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2705">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-2706">
              The Reach of Operating Authority
            </h2>

            <div className="space-y-6" data-id="element-2707">
              <div className="bg-green-50 border-l-4 border-green-500 p-6" data-id="element-2708">
                <p className="text-gray-700 mb-4" data-id="element-2709">
                  FMCSA operating authority is also referred to as an{' '}
                  <strong data-id="element-2710">"MC," "FF," or "MX" number</strong>, depending on the
                  type of authority that is granted.
                </p>
                <p className="text-gray-700" data-id="element-2711">
                  Unlike the USDOT Number application process, a company may
                  need to obtain <strong data-id="element-2712">multiple operating authorities</strong>{' '}
                  to support its planned business operations.
                </p>
              </div>

              <div className="bg-blue-50 rounded-lg p-6" data-id="element-2713">
                <h3 className="font-bold text-gray-900 mb-4" data-id="element-2714">
                  Operating Authority Dictates:
                </h3>
                <div className="grid md:grid-cols-3 gap-4" data-id="element-2715">
                  <div className="bg-white rounded-lg p-4" data-id="element-2716">
                    <p className="font-semibold text-blue-900 mb-2" data-id="element-2717">
                      Type of Operation
                    </p>
                    <p className="text-sm text-gray-700" data-id="element-2718">
                      What kind of business operations a company may run
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4" data-id="element-2719">
                    <p className="font-semibold text-green-900 mb-2" data-id="element-2720">
                      Cargo Type
                    </p>
                    <p className="text-sm text-gray-700" data-id="element-2721">
                      What cargo the company may carry
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4" data-id="element-2722">
                    <p className="font-semibold text-purple-900 mb-2" data-id="element-2723">
                      Geographic Area
                    </p>
                    <p className="text-sm text-gray-700" data-id="element-2724">
                      Where the company may legally operate
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Responsibility and Exemptions */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2725">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2726">
              <Shield className="mr-3 text-[#B1050B]" size={28} data-id="element-2727" />
              Financial Responsibility and Exemptions
            </h2>

            <div className="space-y-6" data-id="element-2728">
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-2729">
                <p className="text-gray-700" data-id="element-2730">
                  All of this also dictates the level of{' '}
                  <strong data-id="element-2731">insurance/financial responsibilities</strong> a
                  company must maintain.
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-2732">
                <h3 className="font-bold text-gray-900 mb-4" data-id="element-2733">
                  Carriers NOT Required to Have Operating Authority:
                </h3>

                <div className="space-y-3" data-id="element-2734">
                  <div className="bg-white rounded-lg p-4 flex items-start" data-id="element-2735">
                    <span className="text-green-600 mr-3 flex-shrink-0 text-xl" data-id="element-2736">
                      ✓
                    </span>
                    <div data-id="element-2737">
                      <p className="font-semibold text-gray-900" data-id="element-2738">
                        Private Carriers
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2739">
                        Companies transporting their own goods
                      </p>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 flex items-start" data-id="element-2740">
                    <span className="text-green-600 mr-3 flex-shrink-0 text-xl" data-id="element-2741">
                      ✓
                    </span>
                    <div data-id="element-2742">
                      <p className="font-semibold text-gray-900" data-id="element-2743">
                        "For-Hire" Carriers with Exempt Commodities
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2744">
                        Exclusively hauling cargo that is not federally
                        regulated
                      </p>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 flex items-start" data-id="element-2745">
                    <span className="text-green-600 mr-3 flex-shrink-0 text-xl" data-id="element-2746">
                      ✓
                    </span>
                    <div data-id="element-2747">
                      <p className="font-semibold text-gray-900" data-id="element-2748">
                        Commercial Zone Operators
                      </p>
                      <p className="text-sm text-gray-600" data-id="element-2749">
                        Operating exclusively within a federally designated
                        "commercial zone" exempt from interstate authority rules
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-6" data-id="element-2750">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-2751">
                  What is a Commercial Zone?
                </h3>
                <p className="text-gray-700" data-id="element-2752">
                  A commercial zone is a geographic territory that includes
                  multiple states bordering on a major metropolitan city.
                </p>
                <p className="text-sm text-gray-600 mt-2 italic" data-id="element-2753">
                  Example: Virginia/Maryland/Washington, DC area
                </p>
              </div>
            </div>
          </div>

          {/* Part 365 Regulations */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-2754">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-2755">
              <FileText className="mr-3 text-[#B1050B]" size={28} data-id="element-2756" />
              Part 365: Rules Governing Applications for Operating Authority
            </h2>

            <div className="space-y-6" data-id="element-2757">
              {/* Subpart A */}
              <div data-id="element-2758">
                <h3 className="text-xl font-bold text-blue-900 mb-4 bg-blue-50 p-3 rounded-lg" data-id="element-2759">
                  Subpart A - How to Apply for Operating Authority
                </h3>
                <div className="space-y-2" data-id="element-2760">
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.101&section_toc=1039" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2761">
                    <div className="flex items-center" data-id="element-2762">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2763">
                        365.101
                      </span>
                      <span className="text-gray-900" data-id="element-2764">
                        Applications governed by these rules
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2765" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.103&section_toc=1040" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2766">
                    <div className="flex items-center" data-id="element-2767">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2768">
                        365.103
                      </span>
                      <span className="text-gray-900" data-id="element-2769">Modified procedure</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2770" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.105&section_toc=1041" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2771">
                    <div className="flex items-center" data-id="element-2772">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2773">
                        365.105
                      </span>
                      <span className="text-gray-900" data-id="element-2774">
                        Starting the application process: Form OP-1
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2775" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.107&section_toc=1042" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2776">
                    <div className="flex items-center" data-id="element-2777">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2778">
                        365.107
                      </span>
                      <span className="text-gray-900" data-id="element-2779">
                        Types of applications
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2780" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.109&section_toc=1043" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2781">
                    <div className="flex items-center" data-id="element-2782">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2783">
                        365.109
                      </span>
                      <span className="text-gray-900" data-id="element-2784">
                        Commission review of the application
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2785" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.111&section_toc=1044" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2786">
                    <div className="flex items-center" data-id="element-2787">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2788">
                        365.111
                      </span>
                      <span className="text-gray-900" data-id="element-2789">
                        Appeals to rejections of the application
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2790" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.113&section_toc=1045" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2791">
                    <div className="flex items-center" data-id="element-2792">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2793">
                        365.113
                      </span>
                      <span className="text-gray-900" data-id="element-2794">
                        Changing the request for authority or filing
                        supplementary evidence
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2795" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.115&section_toc=1046" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2796">
                    <div className="flex items-center" data-id="element-2797">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2798">
                        365.115
                      </span>
                      <span className="text-gray-900" data-id="element-2799">
                        After publication in the FMCSA Register
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2800" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.117&section_toc=1047" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2801">
                    <div className="flex items-center" data-id="element-2802">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2803">
                        365.117
                      </span>
                      <span className="text-gray-900" data-id="element-2804">
                        Obtaining a copy of the application
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2805" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.119&section_toc=1048" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2806">
                    <div className="flex items-center" data-id="element-2807">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2808">
                        365.119
                      </span>
                      <span className="text-gray-900" data-id="element-2809">
                        Opposed applications
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2810" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.121&section_toc=1049" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2811">
                    <div className="flex items-center" data-id="element-2812">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2813">
                        365.121
                      </span>
                      <span className="text-gray-900" data-id="element-2814">
                        Filing a reply statement
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2815" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.123&section_toc=1050" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2816">
                    <div className="flex items-center" data-id="element-2817">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2818">
                        365.123
                      </span>
                      <span className="text-gray-900" data-id="element-2819">
                        Applicant withdrawal
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2820" />
                  </a>
                </div>
              </div>

              {/* Subpart B */}
              <div data-id="element-2821">
                <h3 className="text-xl font-bold text-green-900 mb-4 bg-green-50 p-3 rounded-lg" data-id="element-2822">
                  Subpart B - How to Oppose Requests for Authority
                </h3>
                <div className="space-y-2" data-id="element-2823">
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.201&section_toc=1052" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2824">
                    <div className="flex items-center" data-id="element-2825">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2826">
                        365.201
                      </span>
                      <span className="text-gray-900" data-id="element-2827">Definitions</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2828" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.203&section_toc=1053" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2829">
                    <div className="flex items-center" data-id="element-2830">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2831">
                        365.203
                      </span>
                      <span className="text-gray-900" data-id="element-2832">Time for filing</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2833" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.205&section_toc=1054" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2834">
                    <div className="flex items-center" data-id="element-2835">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2836">
                        365.205
                      </span>
                      <span className="text-gray-900" data-id="element-2837">
                        Contents of the protest
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2838" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.207&section_toc=1055" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2839">
                    <div className="flex items-center" data-id="element-2840">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2841">
                        365.207
                      </span>
                      <span className="text-gray-900" data-id="element-2842">Withdrawal</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2843" />
                  </a>
                </div>
              </div>

              {/* Subpart C */}
              <div data-id="element-2844">
                <h3 className="text-xl font-bold text-purple-900 mb-4 bg-purple-50 p-3 rounded-lg" data-id="element-2845">
                  Subpart C - General Rules Governing the Application Process
                </h3>
                <div className="space-y-2" data-id="element-2846">
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.301&section_toc=1057" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2847">
                    <div className="flex items-center" data-id="element-2848">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2849">
                        365.301
                      </span>
                      <span className="text-gray-900" data-id="element-2850">Applicable rules</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2851" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.303&section_toc=1058" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2852">
                    <div className="flex items-center" data-id="element-2853">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2854">
                        365.303
                      </span>
                      <span className="text-gray-900" data-id="element-2855">
                        Contacting another party
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2856" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.305&section_toc=1059" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2857">
                    <div className="flex items-center" data-id="element-2858">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2859">
                        365.305
                      </span>
                      <span className="text-gray-900" data-id="element-2860">
                        Serving copies of pleadings
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2861" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.307&section_toc=1060" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2862">
                    <div className="flex items-center" data-id="element-2863">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2864">
                        365.307
                      </span>
                      <span className="text-gray-900" data-id="element-2865">Replies to motions</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2866" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.309&section_toc=1061" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2867">
                    <div className="flex items-center" data-id="element-2868">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2869">
                        365.309
                      </span>
                      <span className="text-gray-900" data-id="element-2870">FAX filings</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2871" />
                  </a>
                </div>
              </div>

              {/* Subpart D */}
              <div data-id="element-2872">
                <h3 className="text-xl font-bold text-orange-900 mb-4 bg-orange-50 p-3 rounded-lg" data-id="element-2873">
                  Subpart D - Transfer of Operating Rights Under 49 U.S.C. 10926
                </h3>
                <div className="space-y-2" data-id="element-2874">
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.401&section_toc=1063" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2875">
                    <div className="flex items-center" data-id="element-2876">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2877">
                        365.401
                      </span>
                      <span className="text-gray-900" data-id="element-2878">Scope of rules</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2879" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.403&section_toc=1064" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2880">
                    <div className="flex items-center" data-id="element-2881">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2882">
                        365.403
                      </span>
                      <span className="text-gray-900" data-id="element-2883">Definitions</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2884" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.405&section_toc=1065" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2885">
                    <div className="flex items-center" data-id="element-2886">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2887">
                        365.405
                      </span>
                      <span className="text-gray-900" data-id="element-2888">Applications</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2889" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.407&section_toc=1066" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2890">
                    <div className="flex items-center" data-id="element-2891">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2892">
                        365.407
                      </span>
                      <span className="text-gray-900" data-id="element-2893">Notice</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2894" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.409&section_toc=1067" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2895">
                    <div className="flex items-center" data-id="element-2896">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2897">
                        365.409
                      </span>
                      <span className="text-gray-900" data-id="element-2898">
                        Commission action and criteria for approval
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2899" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.411&section_toc=1068" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2900">
                    <div className="flex items-center" data-id="element-2901">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2902">
                        365.411
                      </span>
                      <span className="text-gray-900" data-id="element-2903">
                        Responsive pleadings
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2904" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.413&section_toc=1069" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2905">
                    <div className="flex items-center" data-id="element-2906">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2907">
                        365.413
                      </span>
                      <span className="text-gray-900" data-id="element-2908">
                        Procedures for changing name or business form
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2909" />
                  </a>
                </div>
              </div>

              {/* Subpart E */}
              <div data-id="element-2910">
                <h3 className="text-xl font-bold text-red-900 mb-4 bg-red-50 p-3 rounded-lg" data-id="element-2911">
                  Subpart E - Special Rules for Certain Mexico-domiciled
                  Carriers
                </h3>
                <div className="space-y-2" data-id="element-2912">
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.501&section_toc=118438" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2913">
                    <div className="flex items-center" data-id="element-2914">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2915">
                        365.501
                      </span>
                      <span className="text-gray-900" data-id="element-2916">Scope of rules</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2917" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.503&section_toc=118439" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2918">
                    <div className="flex items-center" data-id="element-2919">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2920">
                        365.503
                      </span>
                      <span className="text-gray-900" data-id="element-2921">Application</span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2922" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.505&section_toc=118440" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2923">
                    <div className="flex items-center" data-id="element-2924">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2925">
                        365.505
                      </span>
                      <span className="text-gray-900" data-id="element-2926">
                        Re-registration and fee waiver for certain applicants
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2927" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.507&section_toc=118441" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2928">
                    <div className="flex items-center" data-id="element-2929">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2930">
                        365.507
                      </span>
                      <span className="text-gray-900" data-id="element-2931">
                        FMCSA action on the application
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2932" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.509&section_toc=118442" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2933">
                    <div className="flex items-center" data-id="element-2934">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2935">
                        365.509
                      </span>
                      <span className="text-gray-900" data-id="element-2936">
                        Requirement to notify FMCSA of change in applicant
                        information
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2937" />
                  </a>
                  <a href="http://www.fmcsa.dot.gov/rules-regulations/administration/fmcsr/fmcsrruletext.asp?rule_toc=735&section=365.511&section_toc=118443" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-gray-50 hover:bg-gray-100 p-3 rounded transition-colors group" data-id="element-2938">
                    <div className="flex items-center" data-id="element-2939">
                      <span className="font-semibold text-gray-700 mr-3" data-id="element-2940">
                        365.511
                      </span>
                      <span className="text-gray-900" data-id="element-2941">
                        Requirement for CVSA inspection during first 3 years
                      </span>
                    </div>
                    <ExternalLink className="text-gray-400 group-hover:text-blue-600" size={16} data-id="element-2942" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-2943">
            <h2 className="text-3xl font-bold mb-4" data-id="element-2944">
              Need Help with Operating Authority?
            </h2>
            <p className="text-xl mb-6" data-id="element-2945">
              Expert guidance on interstate commerce requirements and FMCSA
              compliance
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-2946">
              <Phone className="mr-3" size={24} data-id="element-2947" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-2948">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}